import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'

export type FeatureOnboardingBubblePlacement = 'bottom' | 'right'

interface IFeatureOnboardingBubbleProps {
    placement?: FeatureOnboardingBubblePlacement
    kicker: string
    title: string
    text: React.ReactNode
    primaryLabel?: string
    onPrimary: () => void
    ghostLabel?: string
    onGhost?: () => void
    anchorRef?: React.RefObject<HTMLElement | null>
    repositionKey?: string | number | boolean
}

const accentBlue = '#5964ff'

const FeatureOnboardingBubble: React.FC<IFeatureOnboardingBubbleProps> = ({
    placement = 'bottom',
    kicker,
    title,
    text,
    primaryLabel = 'Got it',
    onPrimary,
    ghostLabel,
    onGhost,
    anchorRef,
    repositionKey,
}) => {
    const [fixedCoords, setFixedCoords] = useState<{ top: number; left: number } | null>(null)

    const updateFixedCoords = useCallback(() => {
        const anchor = anchorRef?.current
        if (!anchor) return

        const rect = anchor.getBoundingClientRect()
        if (placement === 'right') {
            setFixedCoords({
                top: rect.top + rect.height / 2,
                left: rect.right + 14,
            })
            return
        }

        setFixedCoords({
            top: rect.bottom + 14,
            left: rect.right - 8,
        })
    }, [anchorRef, placement])

    useEffect(() => {
        if (!anchorRef) return

        updateFixedCoords()
        window.addEventListener('resize', updateFixedCoords)
        window.addEventListener('scroll', updateFixedCoords, true)

        return () => {
            window.removeEventListener('resize', updateFixedCoords)
            window.removeEventListener('scroll', updateFixedCoords, true)
        }
    }, [anchorRef, updateFixedCoords, repositionKey])

    const bubble = (
        <Bubble
            $placement={placement}
            $fixed={!!anchorRef}
            style={
                anchorRef && fixedCoords
                    ? {
                          top: fixedCoords.top,
                          left: fixedCoords.left,
                          transform: placement === 'right' ? 'translateY(-50%)' : undefined,
                      }
                    : undefined
            }
        >
            <Kicker>{kicker}</Kicker>
            <Title>{title}</Title>
            <Text>{text}</Text>
            <Actions>
                {ghostLabel && onGhost ? (
                    <GhostBtn type="button" onClick={onGhost}>
                        {ghostLabel}
                    </GhostBtn>
                ) : null}
                <PrimaryBtn type="button" onClick={onPrimary}>
                    {primaryLabel}
                </PrimaryBtn>
            </Actions>
        </Bubble>
    )

    if (anchorRef) {
        if (!fixedCoords) return null
        return ReactDOM.createPortal(bubble, document.body)
    }

    return bubble
}

const placementStyles = {
    bottom: css`
        position: absolute;
        top: calc(100% + 14px);
        right: -8px;

        &::before {
            top: -7px;
            right: 21px;
            transform: rotate(45deg);
        }
    `,
    right: css`
        position: absolute;
        left: calc(100% + 14px);
        top: 50%;
        transform: translateY(-50%);

        &::before {
            left: -7px;
            top: 50%;
            transform: translateY(-50%) rotate(-45deg);
        }
    `,
}

const fixedPlacementStyles = {
    bottom: css`
        position: fixed;
        transform: translateX(-100%);

        &::before {
            top: -7px;
            right: 21px;
            transform: rotate(45deg);
        }
    `,
    right: css`
        position: fixed;

        &::before {
            left: -7px;
            top: 50%;
            transform: translateY(-50%) rotate(-45deg);
        }
    `,
}

const Bubble = styled.div<{
    $placement: FeatureOnboardingBubblePlacement
    $fixed: boolean
}>`
    width: 286px;
    padding: 16px;
    border-radius: 18px;
    background:
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 42%),
        linear-gradient(135deg, #ffffff 0%, #f5f7ff 48%, #edf9ff 100%);
    border: 1px solid rgba(100, 103, 242, 0.18);
    box-shadow: 0 18px 42px rgba(30, 41, 59, 0.18), 0 6px 16px rgba(89, 100, 255, 0.12);
    z-index: 1200;
    color: #41495e;

    ${({ $placement, $fixed }) => ($fixed ? fixedPlacementStyles[$placement] : placementStyles[$placement])}

    &::before {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        background: #ffffff;
        border-left: 1px solid rgba(100, 103, 242, 0.18);
        border-top: 1px solid rgba(100, 103, 242, 0.18);
    }

    &::after {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 19px;
        padding: 1px;
        background: linear-gradient(
            115deg,
            rgba(100, 103, 242, 0),
            rgba(100, 103, 242, 0.9),
            rgba(48, 215, 186, 0.75),
            rgba(100, 103, 242, 0)
        );
        pointer-events: none;
        opacity: 0.85;
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        animation: feature-onboarding-shimmer 2200ms ease-in-out infinite;
    }

    @keyframes feature-onboarding-shimmer {
        0%,
        100% {
            opacity: 0.35;
        }
        50% {
            opacity: 0.95;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        &::after {
            animation: none !important;
        }
    }
`

const Kicker = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 9999px;
    background: rgba(100, 103, 242, 0.1);
    color: ${accentBlue};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`

const Title = styled.div`
    margin-top: 10px;
    font-size: 16px;
    font-weight: 800;
    line-height: 21px;
    color: #20283a;
`

const Text = styled.div`
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: #6a7181;
`

export const FeatureOnboardingTextLink = styled.a`
    color: ${accentBlue};
    font-weight: 800;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
        color: #4349e8;
    }
`

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;
`

const GhostBtn = styled.button`
    height: 30px;
    padding: 0 10px;
    border: none;
    background: transparent;
    color: #8c96ad;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover {
        color: #5964ff;
    }
`

const PrimaryBtn = styled.button`
    height: 30px;
    padding: 0 14px;
    border: none;
    border-radius: 9999px;
    background: linear-gradient(135deg, #6467f2 0%, #5964ff 55%, #30d7ba 130%);
    color: white;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(89, 100, 255, 0.24);

    &:hover {
        filter: brightness(0.97);
    }
`

export const FeatureOnboardingTarget = styled.div<{ $highlight?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 100%;
    z-index: ${({ $highlight }) => ($highlight ? 1002 : 1)};
`

export default FeatureOnboardingBubble
